import React, { useState, useEffect, useCallback } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { useLoading } from './contexts/LoadingContext'
import { useTokenExpiration } from './utils/token'
import ServiceStatusBar from './components/layout/ServiceStatusBar'
import Sidebar from './components/layout/Sidebar'
import SessionExpirationModal from './components/layout/SessionExpirationModal'
import GlobalLoadingOverlay from './components/common/GlobalLoadingOverlay'
import LoginPage from './pages/admin/LoginPage'
import CompaniesPage from './pages/admin/CompaniesPage'
import IntegrationsPage from './pages/admin/IntegrationsPage'
import IntegrationMappingPage from './pages/admin/IntegrationMappingPage'
import SimulatorPage from './pages/admin/SimulatorPage'
import SettingsPage from './pages/admin/SettingsPage'
import SQLPage from './pages/admin/SQLPage'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import AdminCreatePasswordPage from './pages/admin/AdminCreatePasswordPage'
import NotFoundPage from './pages/NotFoundPage'
import { toast, ToastContainer } from './components/toast/CustomToast'
import api, { setupLoadingInterceptors } from './utils/api'

const App = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { token, email, login, logout, isDarkMode, toggleDarkMode, isAdmin } = useAuth();
    const { startLoading, stopLoading } = useLoading();
    const [currentIntegration, setCurrentIntegration] = useState(null);
    const [eventsData, setEventsData] = useState([]);
    const [status, setStatus] = useState(null);
    const [showExpirationModal, setShowExpirationModal] = useState(false);
    const [renewingToken, setRenewingToken] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        localStorage.getItem('admin_sidebar_collapsed') === 'true'
    );

    const toggleSidebarCollapsed = () => {
        setSidebarCollapsed((prev) => {
            const next = !prev;
            localStorage.setItem('admin_sidebar_collapsed', next);
            return next;
        });
    };

    // Get current page from location
    const currentPage = location.pathname.replace('/', '') || 'companies';

    // Callback memoizado para quando token expira
    const handleTokenExpired = useCallback(() => {
        logout();
        setShowExpirationModal(false);
    }, [logout]);

    // Token expiration management
    const { timeRemaining, isExpiring } = useTokenExpiration(token, handleTokenExpired);

    // Show expiration modal when expiring
    useEffect(() => {
        if (isExpiring && timeRemaining) {
            setShowExpirationModal(true);
        }
    }, [isExpiring, timeRemaining]);

    // Reset modal when token changes (after renewal)
    useEffect(() => {
        setShowExpirationModal(false);
    }, [token]);

    // Setup loading interceptors
    useEffect(() => {
        setupLoadingInterceptors(startLoading, stopLoading);
    }, [startLoading, stopLoading]);

    // Function to renew session
    const handleRenewSession = useCallback(async () => {
        setRenewingToken(true);
        try {
            const response = await api.post('/auth/refresh');
            login(response.data.access_token, email);
            setTimeout(() => {
                setShowExpirationModal(false);
            }, 100);
            toast.success('Sessão renovada com sucesso!');
        } catch (error) {
            toast.error('Erro ao renovar sessão');
        } finally {
            setRenewingToken(false);
        }
    }, [token, email, login]);

    // Function to handle logout and redirect to login
    const handleLogout = useCallback(() => {
        logout();
        navigate('/login');
    }, [logout, navigate]);

    // Load status
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const response = await api.get('/setup/status', {
                    silentLoading: true // don't show spinner for background polling
                });
                setStatus(response.data);
            } catch (error) {
                // Silently fail - user might not be logged in yet
            }
        };

        if (token) {
            fetchStatus();
            const interval = setInterval(fetchStatus, 30000); // Refresh every 30s
            return () => clearInterval(interval);
        }
    }, [token]);

    // Public routes (no authentication required)
    const publicPaths = ['/admin-create-password', '/admin-not-found']
    const isPublicRoute = publicPaths.some(path => location.pathname.startsWith(path))

    const knownAdminPaths = ['/', '/login', '/companies', '/integrations', '/integration-mapping', '/simulator', '/settings', '/sql', '/admin-users'];
    const isKnownAdminPath = knownAdminPaths.some(p => location.pathname === p || location.pathname === p + '/');

    if (!token && !isPublicRoute) {
        if (!isKnownAdminPath) return <NotFoundPage context="admin" />;
        return <LoginPage onLogin={login} />;
    }

    // Authenticated admin on unknown route — render 404 standalone (no sidebar)
    if (token && !isPublicRoute && !isKnownAdminPath) {
        return <NotFoundPage context="admin" />;
    }

    // Render public routes without admin layout
    if (isPublicRoute) {
        return (
            <>
                <Routes>
                    <Route path="/admin-create-password" element={<AdminCreatePasswordPage />} />
                    <Route path="/admin-not-found" element={<NotFoundPage context="admin" />} />
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={true}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                />
                <GlobalLoadingOverlay />
            </>
        )
    }

    return (
        <>
            <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-950">
                <ServiceStatusBar status={status} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
                <div className={`flex-1 overflow-auto ${sidebarCollapsed ? 'pl-20' : 'pl-72'} transition-[padding] duration-300 ease-in-out`}>
                    <Routes>
                        <Route path="/" element={<Navigate to="/companies" replace />} />
                        <Route path="/login" element={<Navigate to="/companies" replace />} />
                        <Route path="/companies" element={<CompaniesPage />} />
                        <Route path="/integrations" element={
                            <IntegrationsPage
                                eventsData={eventsData}
                                setCurrentIntegration={setCurrentIntegration}
                                onNavigateToMapping={() => navigate('/integration-mapping')}
                            />
                        } />
                        <Route path="/integration-mapping" element={
                            currentIntegration ? (
                                <IntegrationMappingPage
                                    integration={currentIntegration}
                                    token={token}
                                    onBack={() => {
                                        navigate('/integrations');
                                        setCurrentIntegration(null);
                                    }}
                                />
                            ) : (
                                <Navigate to="/integrations" replace />
                            )
                        } />
                        <Route path="/simulator" element={<SimulatorPage />} />
                        <Route path="/settings" element={<SettingsPage eventsData={eventsData} setEventsData={setEventsData} />} />
                        <Route path="/sql" element={isAdmin ? <SQLPage /> : <Navigate to="/admin-not-found" replace />} />
                        <Route path="/admin-users" element={isAdmin ? <AdminUsersPage /> : <Navigate to="/admin-not-found" replace />} />
                    </Routes>
                </div>
                <div className={`fixed left-0 top-10 bottom-0 ${sidebarCollapsed ? 'w-20' : 'w-72'} bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col z-40 overflow-hidden transition-[width] duration-300 ease-in-out`}>
                    <Sidebar
                        currentPage={currentPage}
                        onPageChange={(page) => navigate(`/${page}`)}
                        userEmail={email}
                        onRenew={handleRenewSession}
                        isRenewing={renewingToken}
                        onLogout={handleLogout}
                        collapsed={sidebarCollapsed}
                        onToggleCollapsed={toggleSidebarCollapsed}
                    />
                </div>
            </div>
            <SessionExpirationModal
                isOpen={showExpirationModal}
                timeRemaining={timeRemaining}
                onRenew={handleRenewSession}
                onLogout={handleLogout}
                loading={renewingToken}
            />
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <GlobalLoadingOverlay />
        </>
    );
};

export default App
